<script
  lang="ts"
  setup
>
import {
  computed,
  type ComputedRef,
  inject,
  onMounted,
  ref,
  removeLocaleFromPath,
  toBlog,
  useI18n,
  useRoute,
} from '#imports'
import { ElPopover } from 'element-plus'
import { type CSSProperties } from 'vue'
import DownIcon from '~/components/icons/DownIcon.vue'
import HeaderBarAvatar from '~/layouts/common/HeaderBarAvatar.vue'
import { useUserInfo } from '~/store/userInfo'
import $track from '~/track/webOfficialWebsite'
import {
  InjectionKey,
  RoutePath,
  RoutePathOfDrive,
} from '~/types/enum'
import I18nMenu from './I18nMenu.vue'
import LogoSVG from './LogoSVG.vue'
import topBarLinkClick from './topBarLinkClick'

const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)

const { t } = useI18n()
const userInfo = useUserInfo()

const path = computed(() => useRoute().path) as ComputedRef<RoutePath>

/* 如果路由中不携带语言参数，此时导航中的路由也会携带语言参数，此时也需要高亮对应的路由，所以有这个方法，比较的时候，移除路由中的语言参数 */
const activeNuxtLink = (currentPath: RoutePath) => (removeLocaleFromPath(path.value) === currentPath
  ? 'active'
  : undefined)
const isHomePath = computed(() => removeLocaleFromPath(path.value) === RoutePath.HOME)

const headerStyle = computed<CSSProperties | undefined>(() => (
  isHomePath.value
    ? undefined
    : {
      background: '#fff',
      borderBottom: 'solid 1px rgba(0, 0, 0, 0.08)',
    }
))

const platformNavRef = ref<HTMLElement>()
const platformPopoverVisible = ref(false)

const supportNavRef = ref<HTMLElement>()
const supportPopoverVisible = ref(false)

const scrollTop = ref<number>(0)

type PlatformNavItem = {
  path: RoutePathOfDrive;
  iconClass: string;
  name: string;
  drivePath: true;
} | {
  path: RoutePath;
  iconClass: string;
  name: string;
  drivePath: undefined;
}
const platformNavItems = computed<PlatformNavItem[]>(() => [
  {
    path: RoutePath.MOBILE_APP,
    iconClass: 'mobile-app',
    name: t('basic-mobile-app'),
  },
  {
    path: RoutePath.DESKTOP_APP,
    iconClass: 'desktop-app',
    name: t('basic-desktop-app'),
  },
  {
    path: RoutePath.TV_APP,
    iconClass: 'tv-app',
    name: t('tv-client'),
  },
  {
    path: RoutePathOfDrive.ALL,
    iconClass: 'web',
    name: t('basic-web-app'),
    drivePath: true,
  },
  {
    path: RoutePath.BROWSER_EXTENSION,
    iconClass: 'browser-extension',
    name: t('browser-extension'),
  },

])

const supportNavItems = computed<{ path: RoutePath; name: string }[]>(() => [
  {
    path: RoutePath.HELP_CENTER,
    name: t('help-center'),
  },
  {
    path: RoutePath.FAQ,
    name: 'FAQ',
  },
  {
    path: RoutePath.BLOG,
    name: t('zpkOoV1ZakY1xxk4B06Ze'),
  },
  {
    path: RoutePath.CONTACT_US,
    name: t('basic-contact-us'),
  },
])

const isPlatformActive = computed(() => platformNavItems.value
  .filter(el => !el.drivePath)
  .map(el => el.path)
  .includes(removeLocaleFromPath(path.value)))
const isSupportActive = computed(() => supportNavItems.value
  .map(el => el.path)
  .includes(removeLocaleFromPath(path.value)))

const arrowIconStyle = (visible: boolean) => ({
  transform: `scale(0.8) rotate(${visible ? '180deg' : 0})`,
  transition: 'transform var(--transition-delay) var(--transition-animation)',
})

const trackMainPageClick = (trackName: Parameters<typeof $track.web_official_main_page_click>[0]['button']) => {
  $track.web_official_main_page_click({ button: trackName })
}

const isOverSlidingHeight = computed(() => scrollTop.value > 24)

const handleScroll = () => {
  scrollTop.value = document.documentElement.scrollTop
  window.addEventListener('scroll', () => {
    scrollTop.value = window.scrollY
  }, { passive: true })
}

onMounted(() => {
  handleScroll()
})
</script>

<template>
  <header
    key="pc_header"
    :class="{ 'over-sliding-height': isOverSlidingHeight }"
    :style="headerStyle"
  >
    <div class="header-content">
      <div>
        <NuxtLinkLocale
          :to="RoutePath.HOME"
          style="display: flex"
        >
          <LogoSVG />
        </NuxtLinkLocale>
        <nav class="nav">
          <ul>
            <li>
              <NuxtLinkLocale
                :class="activeNuxtLink(RoutePath.HOME)"
                :to="RoutePath.HOME"
                class="nav-item"
                @click="topBarLinkClick(RoutePath.HOME)"
              >
                {{ $t('basic-home-page') }}
              </NuxtLinkLocale>
            </li>
            <li>
              <ClientOnly>
                <ElPopover
                  v-model:visible="platformPopoverVisible"
                  :teleported="false"
                  :virtual-ref="platformNavRef"
                  popper-class="pp-nav-popover --el-popover platform-popover"
                  trigger="hover"
                  virtual-triggering
                >
                  <ul>
                    <li
                      v-for="item in platformNavItems"
                      :key="item.path"
                      @click="
                        platformPopoverVisible = false,
                        topBarLinkClick(item.path)
                      "
                    >
                      <a
                        v-if="item.drivePath"
                        :href="item.path"
                      >
                        <span
                          :class="item.iconClass"
                          class="icon"
                        />
                        {{ item.name }}
                      </a>
                      <NuxtLinkLocale
                        v-else
                        :class="activeNuxtLink(item.path)"
                        :to="item.path"
                      >
                        <span
                          :class="item.iconClass"
                          class="icon"
                        />
                        {{ item.name }}
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </ElPopover>
              </ClientOnly>
              <a
                ref="platformNavRef"
                :class="(isPlatformActive || platformPopoverVisible) && 'active'"
                class="nav-item"
                href="javascript:void(0)"
              >
                {{ $t('basic-platform') }}
                <DownIcon :style="arrowIconStyle(platformPopoverVisible)" />
              </a>
            </li>
            <li>
              <a
                :href="RoutePathOfDrive.PAYMENT"
                class="nav-item"
              >
                {{ $t('basic-price') }}
              </a>
            </li>
            <li>
              <a
                class="nav-item"
                target="_blank"
                :href="RoutePathOfDrive.REFERRAL"
              >{{ $t('akeb2cKjhVg3fG9rvBhY') }}</a>
            </li>
            <li>
              <ClientOnly>
                <ElPopover
                  v-model:visible="supportPopoverVisible"
                  :teleported="false"
                  :virtual-ref="supportNavRef"
                  popper-class="pp-nav-popover --el-popover support-popover"
                  trigger="hover"
                  virtual-triggering
                >
                  <ul>
                    <li
                      v-for="item in supportNavItems"
                      :key="item.path"
                      @click="
                        supportPopoverVisible = false,
                        topBarLinkClick(item.path)
                      "
                    >
                      <a
                        v-if="item.path === RoutePath.BLOG"
                        :href="toBlog()"
                        target="_blank"
                      >{{ item.name }}</a>
                      <NuxtLinkLocale
                        v-else
                        :class="activeNuxtLink(item.path)"
                        :to="item.path"
                      >
                        {{ item.name }}
                      </NuxtLinkLocale>
                    </li>
                  </ul>
                </ElPopover>
              </ClientOnly>
              <a
                ref="supportNavRef"
                :class="(isSupportActive || supportPopoverVisible) && 'active'"
                class="nav-item"
                href="javascript:void(0)"
              >
                {{ $t('basic-support') }}
                <DownIcon :style="arrowIconStyle(supportPopoverVisible)" />
              </a>
            </li>
          </ul>
        </nav>
      </div>
      <div class="info">
        <I18nMenu />
        <div
          v-if="userInfo.userId"
          class="avatar-container"
        >
          <HeaderBarAvatar />
          <a
            :href="RoutePathOfDrive.ALL"
            @click="trackMainPageClick('go_to_pikpak')"
          >
            {{ $t('visit-web') }}
          </a>
          {{ }}
        </div>
        <ElButton
          v-else
          class="pp-link-button sign-up-in"
          @click="() => showLoginDialog?.()"
        >
          {{ $t('basic-sign-up-or-in') }}
        </ElButton>
      </div>
    </div>
  </header>
</template>

<style
  lang="less"
  scoped
>
@import 'assets/styles/util.less';

header {
  width: 100%;
  position: relative;
  z-index: 2;
  transform: translate3d(0, 0, 0);
  margin-bottom: -61px; // NOTICE 加上 1px 的底边框

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: var(--header-height);
    max-width: calc(var(--pc-max-width) + 40px);
    padding: 0 20px;
    margin: auto;

    > div {
      display: flex;
      align-items: center;
    }
  }

  .nav {
    transition: margin-left var(--transition-delay) var(--transition-animation);
    margin-left: 48px;

    > ul {
      display: flex;
      align-items: center;
      gap: 32px;
      transition: gap var(--transition-delay) var(--transition-animation);
    }
  }

  .nav-item {
    min-width: 55px;
    padding: 0 8px;
    height: var(--header-height);
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary-text);
    text-decoration: none;
    text-align: center;
    position: relative;

    &:after {
      content: '';
      display: block;
      position: absolute;
      height: 2px;
      left: 50%;
      right: 50%;
      bottom: 0;
      background: var(--color-primary);
      transition: all var(--transition-animation) var(--transition-delay);
    }

    &:active,
    &:hover,
    &.active {
      &:after {
        left: 0;
        right: 0;
      }
    }
  }

  .info {
    .avatar-container {
      transition: margin-left var(--transition-delay) var(--transition-animation);
      margin-left: 48px;
      display: flex;
      align-items: center;

      a {
        #ellipsis();
        max-width: 180px;
        text-decoration: none;
        border: 1px solid #306eff;
        border-radius: 4px;
        margin-left: 24px;
        color: #306eff;
        font-weight: 600;
        font-size: 14px;
        line-height: 22px;
        padding: 7px 5.5px;
      }
    }

    .el-button {
      background: none;
    }
  }

  .sign-up-in {
    margin-left: 30px;
    margin-right: 10px;
    padding: 16px;
    border: 1px solid var(--color-primary);
    border-radius: 4px;
    font-weight: 600;
    color: var(--color-primary) !important;
  }

  &.over-sliding-height {
    border-bottom: solid 1px rgba(0, 0, 0, 0.08);
    background: #fff;
    position: fixed;
    margin-bottom: 0;
    top: 0;
  }
}

@media screen and (max-width: 1300px) {
  header {
    .nav {
      margin-left: 24px;

      > ul {
        gap: 12px;
      }
    }

    .info .avatar-container {
      margin-left: 24px;
    }
  }
}

</style>
<style lang="less">
.pp-nav-popover {
  min-width: 200px !important;
  width: auto !important;
  padding: 8px !important;

  ul {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
  }

  a {
    display: flex;
    align-items: center;
    height: 52px;
    border-radius: 8px;
    font-size: 16px;
    color: var(--color-primary-text);
    text-decoration: none;
    padding: 0 12px 0 8px;

    &:hover {
      background: var(--color-hover);
    }

    &.active {
      background: var(--color-active);
    }
  }

  .icon {
    height: 42px;
    width: 42px;
    margin-right: 12px;

    @icons: mobile-app, desktop-app, web, browser-extension, tv-app;

    each(@icons, {
      &.@{value} {
        @img: '@/assets/images/basic-layout/@{value}.svg';
        background: url('@{img}');
      }
    });
  }
}
</style>
